import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = ["id"];
const _hoisted_2 = { class: "text-sm text-(--colors-text-body)" };
const _hoisted_3 = ["id"];
import { computed, h, ref, useId } from "vue";
import { useI18n } from "vue-i18n";
import { Badge, Button, Dropdown, Modal, RecordsTable, Tooltip, WidgetShell } from "@eudonet/opal";
import { useAppToast } from "@/shared/composables/useAppToast";
import { useProfileStore } from "@/modules/settings/public";
import { formatDate as formatDateWithProfile } from "@/shared/utils/date-formatting";
import { TruncatedCell } from "@/shared/components/TruncatedCell";
import { deleteMilestone } from "../../../infrastructure/api/projectsApi";
import { milestoneStatusTheme } from "../../composables/milestoneStatusBadge";
import MilestoneFormModal from "./MilestoneFormModal.vue";
export default /*@__PURE__*/ _defineComponent({
    __name: 'ProjectMilestonesWidget',
    props: {
        projectId: {},
        milestones: {},
        canEdit: { type: Boolean }
    },
    emits: ["saved", "deleted"],
    setup(__props, { expose: __expose, emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const { t, locale } = useI18n();
        const { toastSuccess, toastError } = useAppToast();
        const profileStore = useProfileStore();
        const deleteTitleId = useId();
        const expandTitleId = useId();
        const formOpen = ref(false);
        const editTarget = ref(null);
        const expandOpen = ref(false);
        function openCreate() {
            editTarget.value = null;
            formOpen.value = true;
        }
        // The Résumé's "Ajouter un jalon" switches to this tab and opens the creation modal from here,
        // so the trigger has to be reachable from the parent view.
        __expose({ openCreate });
        function openEdit(milestone) {
            editTarget.value = milestone;
            formOpen.value = true;
        }
        const deleteOpen = ref(false);
        const deleteTarget = ref(null);
        const deleting = ref(false);
        function onDelete(milestone) {
            deleteTarget.value = milestone;
            deleteOpen.value = true;
        }
        async function confirmDelete() {
            const target = deleteTarget.value;
            if (!target || deleting.value)
                return;
            deleting.value = true;
            try {
                await deleteMilestone(props.projectId, target.milestoneId);
                toastSuccess("projects.detail.milestones.toastDeleted");
                deleteOpen.value = false;
                emit("deleted", target.milestoneId);
            }
            catch {
                toastError("projects.detail.milestones.deleteError");
            }
            finally {
                deleting.value = false;
            }
        }
        const columns = computed(() => {
            const base = [
                {
                    id: "status",
                    header: t("projects.detail.milestones.columns.status"),
                    width: "w-32",
                    cell: (row) => h(Badge, {
                        label: t(`projects.milestoneStatus.${row.status}`),
                        theme: milestoneStatusTheme[row.status] ?? "neutral",
                        showCloseIcon: false,
                        size: "sm",
                        truncate: false,
                    }),
                },
                {
                    id: "label",
                    header: t("projects.detail.milestones.columns.label"),
                    width: "min-w-40",
                    cell: (row) => h(TruncatedCell, {
                        text: row.label,
                        textClass: "text-sm text-(--text-text-black-default)",
                    }),
                },
                {
                    id: "targetDate",
                    header: t("projects.detail.milestones.columns.targetDate"),
                    width: "w-32",
                    cell: (row) => h("span", { class: "text-sm text-(--text-text-black-default)" }, row.targetDate
                        ? formatDateWithProfile(row.targetDate, locale.value, profileStore.profile?.dateFormat)
                        : "–"),
                },
            ];
            if (props.canEdit) {
                base.push({
                    id: "actions",
                    header: "",
                    width: "w-18",
                    cell: (row) => h(Dropdown, {
                        ghost: true,
                        label: "",
                        triggerIconLeft: "ri-more-line",
                        menuAlign: "right",
                        menuWidth: "127px",
                        ariaLabel: t("projects.detail.milestones.rowActions"),
                        items: [
                            {
                                label: t("projects.detail.milestones.actions.edit"),
                                icon: "ri-pencil-line",
                                onClick: () => openEdit(row),
                            },
                            {
                                label: t("projects.detail.milestones.actions.delete"),
                                icon: "ri-delete-bin-line",
                                danger: true,
                                onClick: () => onDelete(row),
                            },
                        ],
                    }),
                });
            }
            return base;
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(_unref(WidgetShell), {
                    header: _unref(t)('projects.detail.milestones.title')
                }, {
                    actions: _withCtx(() => [
                        _createVNode(_unref(Badge), {
                            label: String(__props.milestones.length),
                            theme: "neutral",
                            "show-close-icon": false,
                            size: "sm",
                            "data-testid": "milestones-count"
                        }, null, 8 /* PROPS */, ["label"]),
                        _createVNode(_unref(Tooltip), {
                            text: _unref(t)('common.permissions.noEditRights'),
                            disabled: __props.canEdit,
                            tabindex: __props.canEdit ? undefined : 0
                        }, {
                            default: _withCtx(() => [
                                _createVNode(_unref(Button), {
                                    variant: "primary",
                                    size: "sm",
                                    "icon-left": "ri-add-line",
                                    label: _unref(t)('projects.detail.milestones.add'),
                                    disabled: !__props.canEdit,
                                    "data-testid": "milestone-add-button",
                                    onClick: openCreate
                                }, null, 8 /* PROPS */, ["label", "disabled"])
                            ]),
                            _: 1 /* STABLE */
                        }, 8 /* PROPS */, ["text", "disabled", "tabindex"]),
                        _createVNode(_unref(Button), {
                            variant: "secondary",
                            "icon-only": "",
                            "icon-left": "ri-expand-diagonal-fill",
                            "aria-label": _unref(t)('projects.detail.milestones.expand'),
                            "data-testid": "milestones-expand-button",
                            onClick: _cache[0] || (_cache[0] = ($event) => (expandOpen.value = true))
                        }, null, 8 /* PROPS */, ["aria-label"])
                    ]),
                    default: _withCtx(() => [
                        _createVNode(_unref(RecordsTable), {
                            columns: columns.value,
                            data: __props.milestones,
                            "table-class": 'min-w-120',
                            "empty-text": _unref(t)('projects.detail.milestones.empty')
                        }, null, 8 /* PROPS */, ["columns", "data", "empty-text"])
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["header"]),
                _createVNode(MilestoneFormModal, {
                    modelValue: formOpen.value,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((formOpen).value = $event)),
                    "project-id": __props.projectId,
                    milestone: editTarget.value,
                    onSaved: _cache[2] || (_cache[2] = (milestone) => _ctx.$emit('saved', milestone))
                }, null, 8 /* PROPS */, ["modelValue", "project-id", "milestone"]),
                _createVNode(_unref(Modal), {
                    modelValue: deleteOpen.value,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((deleteOpen).value = $event)),
                    size: "sm",
                    dismissable: !deleting.value,
                    "close-on-escape": !deleting.value,
                    "show-close-button": !deleting.value,
                    "aria-labelledby": _unref(deleteTitleId),
                    "overlay-class": "!z-[300]",
                    "data-testid": "milestone-delete-modal"
                }, {
                    header: _withCtx(() => [
                        _createElementVNode("h3", {
                            id: _unref(deleteTitleId),
                            class: "text-lg font-bold text-(--colors-text-heading)"
                        }, _toDisplayString(_unref(t)("projects.detail.milestones.deleteModal.title")), 9 /* TEXT, PROPS */, _hoisted_1)
                    ]),
                    footer: _withCtx(() => [
                        _createVNode(_unref(Button), {
                            variant: "secondary",
                            label: _unref(t)('common.cancel'),
                            disabled: deleting.value,
                            "data-testid": "milestone-delete-cancel",
                            onClick: _cache[3] || (_cache[3] = ($event) => (deleteOpen.value = false))
                        }, null, 8 /* PROPS */, ["label", "disabled"]),
                        _createVNode(_unref(Button), {
                            variant: "error",
                            label: _unref(t)('projects.detail.milestones.deleteModal.confirm'),
                            loading: deleting.value,
                            "data-testid": "milestone-delete-confirm",
                            onClick: confirmDelete
                        }, null, 8 /* PROPS */, ["label", "loading"])
                    ]),
                    default: _withCtx(() => [
                        _createElementVNode("p", _hoisted_2, _toDisplayString(_unref(t)("projects.detail.milestones.deleteModal.message", { label: deleteTarget.value?.label ?? "" })), 1 /* TEXT */)
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["modelValue", "dismissable", "close-on-escape", "show-close-button", "aria-labelledby"]),
                _createVNode(_unref(Modal), {
                    modelValue: expandOpen.value,
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event) => ((expandOpen).value = $event)),
                    size: "lg",
                    "aria-labelledby": _unref(expandTitleId),
                    "data-testid": "milestones-expand-modal"
                }, {
                    header: _withCtx(() => [
                        _createElementVNode("h3", {
                            id: _unref(expandTitleId),
                            class: "text-lg font-bold text-(--colors-text-heading)"
                        }, _toDisplayString(_unref(t)("projects.detail.milestones.title")), 9 /* TEXT, PROPS */, _hoisted_3)
                    ]),
                    default: _withCtx(() => [
                        _createVNode(_unref(RecordsTable), {
                            columns: columns.value,
                            data: __props.milestones,
                            "empty-text": _unref(t)('projects.detail.milestones.empty')
                        }, null, 8 /* PROPS */, ["columns", "data", "empty-text"])
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["modelValue", "aria-labelledby"])
            ], 64 /* STABLE_FRAGMENT */));
        };
    }
});
